import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { stationCodes as fallbackStations } from '../../data/defaultFare';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const initialState = {
  stations: [],
  matrix: [],
  loading: false,
  error: null,
  savingStatus: 'idle',
  savingError: null,
  uploadStatus: 'idle',
  uploadError: null,
  exportStatus: 'idle',
  exportError: null,
  syncStatus: 'idle',
  syncError: null,
  updatedAt: null
};

const stationOrderMap = {
  BHI: 1,
  DKA: 2,
  STB: 3,
  BNH: 4,
  IST: 5,
  SNY: 6,
  ASN: 7,
  BLM: 8,
  BLA: 9,
  HJN: 10,
  CPR: 11,
  FTM: 12,
  LBB: 13
};

const sortStationsByOrder = (stations) =>
  [...stations].sort((a, b) => {
    const orderA = stationOrderMap[a] ?? Number.MAX_SAFE_INTEGER;
    const orderB = stationOrderMap[b] ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

const normalizeStations = (stations) => {
  if (Array.isArray(stations) && stations.length) {
    return stations.map((station) => String(station).trim()).filter(Boolean);
  }
  return [...fallbackStations];
};

const normalizePrice = (value) => {
  const candidate =
    value && typeof value === 'object' ? value.price ?? value.amount ?? value.value : value;
  const numeric = Number(candidate);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const createBlankMatrix = (stations) =>
  stations.map((origin) =>
    stations.map((destination) => ({
      origin,
      destination,
      price: 0
    }))
  );

const normalizeMatrix = (stations, matrixData) => {
  if (!Array.isArray(stations) || !stations.length) {
    return [];
  }

  if (!Array.isArray(matrixData) || !matrixData.length) {
    return createBlankMatrix(stations);
  }

  const isNestedArray = Array.isArray(matrixData[0]);

  if (isNestedArray) {
    return stations.map((origin, rowIndex) =>
      stations.map((destination, colIndex) => {
        const rawCell = matrixData[rowIndex]?.[colIndex];
        return {
          origin,
          destination,
          price: normalizePrice(rawCell)
        };
      })
    );
  }

  const lookup = new Map();
  matrixData.forEach((cell) => {
    if (!cell || typeof cell !== 'object') return;
    const origin = String(cell.origin ?? '').trim();
    const destination = String(cell.destination ?? '').trim();
    if (!origin || !destination) return;
    const key = `${origin.toUpperCase()}|${destination.toUpperCase()}`;
    lookup.set(key, normalizePrice(cell));
  });

  return stations.map((origin) =>
    stations.map((destination) => {
      const key = `${origin.toUpperCase()}|${destination.toUpperCase()}`;
      return {
        origin,
        destination,
        price: lookup.has(key) ? lookup.get(key) : 0
      };
    })
  );
};

const reorderMatrix = (stations, matrix) => {
  const sortedStations = sortStationsByOrder(stations);
  const lookup = new Map();

  matrix.forEach((row) => {
    row.forEach((cell) => {
      const key = `${String(cell.origin).toUpperCase()}|${String(cell.destination).toUpperCase()}`;
      lookup.set(key, {
        origin: String(cell.origin).toUpperCase(),
        destination: String(cell.destination).toUpperCase(),
        price: normalizePrice(cell.price)
      });
    });
  });

  return {
    stations: sortedStations,
    matrix: sortedStations.map((origin) =>
      sortedStations.map((destination) => {
        const key = `${origin}|${destination}`;
        return (
          lookup.get(key) || {
            origin,
            destination,
            price: 0
          }
        );
      })
    )
  };
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };
  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }
  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

export const fetchDefaultFares = createAsyncThunk(
  'defaultFares/fetchDefaultFares',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/default-fares`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const rawStations = normalizeStations(data?.stations);
      const rawMatrix = normalizeMatrix(rawStations, data?.matrix);
      const { stations, matrix } = reorderMatrix(rawStations, rawMatrix);

      return {
        stations,
        matrix,
        updatedAt: data?.updated_at ?? null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load default fares.');
    }
  }
);

export const updateDefaultFares = createAsyncThunk(
  'defaultFares/updateDefaultFares',
  async ({ matrix }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!Array.isArray(matrix) || !matrix.length) {
        throw new Error('Matrix payload is required.');
      }

      const sanitizedMatrix = matrix.map((row) =>
        row.map((cell) => ({
          origin: cell.origin,
          destination: cell.destination,
          price: Number(cell.price) || 0
        }))
      );

      const flatMatrix = sanitizedMatrix
        .flat()
        .map((cell) => ({
          origin: cell.origin,
          destination: cell.destination,
          price: Number(cell.price) || 0
        }));

      const { auth } = getState();
      const body = {
        matrix: flatMatrix
      };

      if (auth?.user?.id) {
        body.updated_by = auth.user.id;
      }

      const response = await fetch(`${API_BASE_URL}/api/default-fares`, {
        method: 'PUT',
        headers: buildAuthHeaders(getState),
        body: JSON.stringify(body)
      });

      const data = await handleApiResponse(response, dispatch);

      if (Number(data?.resultCode) === 409) {
        const message =
          data?.resultDesc || data?.resultMessage || data?.message || 'Unable to save fares.';
        return rejectWithValue(message);
      }

      return {
        matrix: sanitizedMatrix,
        updatedAt: data?.updated_at ?? null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update default fares.');
    }
  }
);

export const syncDefaultFares = createAsyncThunk(
  'defaultFares/syncDefaultFares',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/default-fares/sync`, {
        method: 'POST',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      if (Number(data?.resultCode) && Number(data.resultCode) !== 200) {
        throw new Error(data?.resultDesc || data?.resultMessage || data?.message);
      }

      return {
        updatedAt: data?.updated_at ?? null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reload default fares.');
    }
  }
);

export const importDefaultFares = createAsyncThunk(
  'defaultFares/importDefaultFares',
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!(file instanceof File)) {
        throw new Error('Fare matrix file is required.');
      }

      const headers = buildAuthHeaders(getState, {
        Accept: 'application/json',
        'Content-Type':
          file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      delete headers['Content-Type'];
      headers['Content-Type'] =
        file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const response = await fetch(`${API_BASE_URL}/api/default-fares/import`, {
        method: 'POST',
        headers,
        body: file
      });

      const data = await handleApiResponse(response, dispatch);
      if (Number(data?.resultCode) && Number(data.resultCode) !== 200) {
        throw new Error(data?.resultDesc || data?.resultMessage || data?.message);
      }

      return {
        updatedAt: data?.updated_at ?? null,
        totalItems: Number(data?.total_items ?? 0)
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to upload fare matrix.');
    }
  }
);

export const exportDefaultFares = createAsyncThunk(
  'defaultFares/exportDefaultFares',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/default-fares/export`, {
        method: 'GET',
        headers: buildAuthHeaders(getState, {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const errorText = await response.text();
        throw new Error(errorText || response.statusText || 'Failed to download fare matrix.');
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = await response.json();
        if (Number(payload?.resultCode) === 401) {
          handleUnauthorized(dispatch);
        }
        if (Number(payload?.resultCode) !== 200) {
          throw new Error(
            payload?.resultDesc ||
              payload?.resultMessage ||
              payload?.message ||
              'Failed to download fare matrix.'
          );
        }
        throw new Error(payload?.resultDesc || 'No file available for download.');
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename = match ? match[1] : 'OD Tariff.xlsx';

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download fare matrix.');
    }
  }
);

const defaultFareSlice = createSlice({
  name: 'defaultFares',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDefaultFares.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDefaultFares.fulfilled, (state, action) => {
        state.loading = false;
        state.stations = action.payload.stations;
        state.matrix = action.payload.matrix;
        state.updatedAt = action.payload.updatedAt;
        state.error = null;
      })
      .addCase(fetchDefaultFares.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load default fares.';
      })
      .addCase(updateDefaultFares.pending, (state) => {
        state.savingStatus = 'loading';
        state.savingError = null;
      })
      .addCase(updateDefaultFares.fulfilled, (state, action) => {
        state.savingStatus = 'succeeded';
        state.savingError = null;
        if (Array.isArray(action.payload.matrix)) {
          state.matrix = action.payload.matrix;
        }
        state.updatedAt = action.payload.updatedAt;
      })
      .addCase(updateDefaultFares.rejected, (state, action) => {
        state.savingStatus = 'failed';
        state.savingError = action.payload || 'Failed to update default fares.';
      })
      .addCase(importDefaultFares.pending, (state) => {
        state.uploadStatus = 'loading';
        state.uploadError = null;
      })
      .addCase(importDefaultFares.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.uploadError = null;
        if (action.payload?.updatedAt) {
          state.updatedAt = action.payload.updatedAt;
        }
      })
      .addCase(importDefaultFares.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.uploadError = action.payload || 'Failed to upload fare matrix.';
      })
      .addCase(exportDefaultFares.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportDefaultFares.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
        state.exportError = null;
      })
      .addCase(exportDefaultFares.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to download fare matrix.';
      })
      .addCase(syncDefaultFares.pending, (state) => {
        state.syncStatus = 'loading';
        state.syncError = null;
      })
      .addCase(syncDefaultFares.fulfilled, (state, action) => {
        state.syncStatus = 'succeeded';
        state.syncError = null;
        if (action.payload?.updatedAt) {
          state.updatedAt = action.payload.updatedAt;
        }
      })
      .addCase(syncDefaultFares.rejected, (state, action) => {
        state.syncStatus = 'failed';
        state.syncError = action.payload || 'Failed to reload default fares.';
      });
  }
});

export default defaultFareSlice.reducer;
